import {Component, ElementRef, Input, OnDestroy, ViewChild} from '@angular/core';
import {Observable, Subscription} from 'rxjs';
import {take} from 'rxjs/operators';
import {Popover} from 'primeng/popover';
import {faCog, faPencil} from '@fortawesome/pro-solid-svg-icons';
import {
    SingleEditEnforcer,
    SingleEditTrackable
} from '@webmate-web-core/shared/components/forms-commons/single-edit-enforcer.service';

@Component({
    selector: 'wm-editable-popover',
    templateUrl: './editable-popover.component.html',
    styleUrls: ['./editable-popover.component.less']
})
export class EditablePopoverComponent<T extends string | boolean> implements OnDestroy, SingleEditTrackable {
    @ViewChild('popover') popover?: Popover;
    @ViewChild('textInput') textInput?: ElementRef<HTMLInputElement>;

    @Input({required: true}) displayValue!: T;
    @Input() options?: readonly T[];
    @Input({required: true}) save!: (newValue: T) => Observable<unknown>;
    @Input() disabled = false;

    draftValue!: T;
    draftText = '';
    saving = false;

    protected readonly faCog = faCog;
    protected readonly faPencil = faPencil;

    private saveSubscription?: Subscription;

    constructor(private readonly singleEditEnforcer: SingleEditEnforcer) {
        this.singleEditEnforcer.registerTrackable(this);
    }

    get displayLabel(): string {
        return this.formatValue(this.displayValue);
    }

    get hasOptions(): boolean {
        return this.options !== undefined;
    }

    get usesOptionButtons(): boolean {
        return this.hasOptions && this.options!.length <= 3;
    }

    get usesSelect(): boolean {
        return this.hasOptions && this.options!.length > 3;
    }

    get usesBooleanToggle(): boolean {
        return !this.hasOptions && typeof this.displayValue === 'boolean';
    }

    get usesTextInput(): boolean {
        return !this.hasOptions && typeof this.displayValue === 'string';
    }

    get saveDisabled(): boolean {
        if (this.saving) {
            return true;
        }

        if (this.usesTextInput) {
            return this.draftText.trim().length === 0 || this.draftText === this.displayValue;
        }

        if (this.hasOptions && !this.options!.includes(this.draftValue)) {
            return true;
        }

        return Object.is(this.draftValue, this.displayValue);
    }

    open(event: Event): void {
        if (this.disabled || this.saving) {
            return;
        }

        this.singleEditEnforcer.disableAllExcept(this);
        this.resetDraft();
        this.popover?.show(event, event.currentTarget);
    }

    focusTextInput(): void {
        if (!this.usesTextInput) {
            return;
        }

        setTimeout(() => {
            this.textInput?.nativeElement.focus();
            this.textInput?.nativeElement.select();
        });
    }

    selectOption(option: T): void {
        if (!this.saving) {
            this.draftValue = option;
        }
    }

    selectBoolean(value: boolean): void {
        if (!this.saving) {
            this.draftValue = value as T;
        }
    }

    saveValue(): void {
        if (this.saveDisabled) {
            return;
        }

        const newValue = (this.usesTextInput ? this.draftText : this.draftValue) as T;
        this.saving = true;
        this.saveSubscription = this.save(newValue).pipe(take(1)).subscribe({
            error: () => this.saving = false,
            complete: () => {
                this.saving = false;
                this.popover?.hide();
            }
        });
    }

    cancel(): void {
        if (!this.saving) {
            this.popover?.hide();
        }
    }

    disableEdit(): void {
        this.popover?.hide();
    }

    resetDraft(): void {
        this.draftValue = this.displayValue;
        this.draftText = typeof this.displayValue === 'string' ? this.displayValue : '';
    }

    formatValue(value: T): string {
        return typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value);
    }

    ngOnDestroy(): void {
        this.saveSubscription?.unsubscribe();
        this.singleEditEnforcer.unregisterTrackable(this);
    }
}
